import React, { useCallback } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "../App";
import { AREAS } from "./areas";
import CipherShell from "./CipherShell";
import ErrorBoundary from "./ErrorBoundary";
import BrainArea from "./routes/brain";
import EAArea from "./routes/ea";
import OperationsArea from "./routes/operations";
import SettingsArea from "./routes/settings";
import WorkstationArea from "./routes/workstation";

/**
 * The v1.1 route table. Registered by `App.js` only when `REACT_APP_CIPHER_V2`
 * is true.
 *
 * WHY THIS FILE EXISTS AT ALL
 * ===========================
 * `App.js` is 4,963 lines and holds every route in the application. Five
 * sessions building five areas cannot each add routes to it — that is one file
 * taking five concurrent edits, and the merge is the following morning's
 * problem rather than the night's work. So `App.js` gains exactly one guarded
 * line, forever, and every v1.1 route is declared here instead. An area session
 * edits `routes/<its area>.jsx` and, if it needs sub-routes, adds them beneath
 * its own `<Route>` below. It never opens `App.js`.
 *
 * ROLE COMES FROM THE SESSION, NOT FROM A PROP
 * ============================================
 * `useAuth()` is `App.js`'s own AuthProvider, already populated from
 * `GET /auth/me` — which returns role, name, operator_id, picture and
 * business_unit_access, and is already called at App.js:83. No new endpoint is
 * needed for role-aware navigation and none is added (R4). Importing `useAuth`
 * from `../App` is the tree's existing convention; `CreativeAnalyzer.jsx` and
 * `OfferCardActions.jsx` both do it. The import is circular by construction and
 * safe because it is dereferenced at render time, not at module evaluation.
 *
 * THE CLIENT GATE IS NAVIGATION, NOT ACCESS CONTROL (R5)
 * ======================================================
 * There is deliberately NO client-side redirect on founder-only tabs here. Two
 * reasons, and the second is the one that matters. First, the routes below
 * render surfaces whose data comes from endpoints the server already gates —
 * `require_founder` appears 37 times across the new routers, and a non-founder
 * typing `/v2/brain/health` gets an empty surface because the API refuses it,
 * which is the honest outcome. Second, adding a client redirect would create a
 * second, weaker gate that looks like the real one; the next person to read it
 * would reasonably assume the client was enforcing something, and R5 exists to
 * prevent exactly that impression.
 *
 * `ProtectedRoute` is not reused here for the same reason it is not reimplemented:
 * it lives in `App.js` and is not exported, and exporting it would be a second
 * change to the file this structure exists to leave alone. Authentication is
 * still enforced — `useAuth()` yields no user when the session is absent, and
 * the shell renders its unauthenticated state rather than a surface.
 */

const AREA_COMPONENTS = {
  brain: BrainArea,
  workstation: WorkstationArea,
  operations: OperationsArea,
  ea: EAArea,
  settings: SettingsArea,
};

/**
 * The unauthenticated state. No surface, no invented content — and, since
 * V2-SIGNOUT, no dead end either.
 *
 * WHY THIS LINKS OUT TO v1 RATHER THAN RENDERING A LOGIN
 * =====================================================
 * v1's `/login` works and v1 is not being retired today, so sending an
 * unauthenticated visitor to it is the correct behaviour for now. A v1.1-native
 * login is a separate piece of work for when v1 actually goes, and building one
 * here would mean two login screens against one auth endpoint.
 *
 * It is a plain anchor rather than a router `Link` on purpose: this is the exit
 * from the v1.1 surface, and a full document load guarantees no v1.1 state
 * survives the sign-out that usually precedes it. `/login` is a real route —
 * `App.js` registers it unguarded, above every `ProtectedRoute`.
 */
function ShellSignedOut({ loading }) {
  if (loading) {
    return (
      <div className="cipher-v2 shell-signed-out">
        <div className="state" role="status">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="cipher-v2 shell-signed-out">
      <div className="state" role="status">
        <div className="state-empty-title">You are not signed in</div>
        <div className="state-empty-detail">
          Cipher needs a session to show anything here.
        </div>
        <div className="state-empty-action">
          <a className="signin-link" href="/login" data-testid="v2-signin-link">
            Go to sign in
          </a>
        </div>
      </div>
    </div>
  );
}

export default function V2Routes() {
  const { user, loading, logout } = useAuth();

  /**
   * The one place in v1.1 that ends a session.
   *
   * `logout` is App.js's own — it POSTs `${API}/auth/logout`, which deletes the
   * operator's server-side sessions and clears the cookie, then drops the user
   * from context. That is the same function v1's own "Sign out" button calls;
   * no second endpoint and no second implementation exist. It swallows its own
   * errors and always clears the client user, so this never throws.
   *
   * Then a hard navigation to `/login`. Clearing context alone would leave the
   * operator on this component's signed-out state, which is a fine place to be
   * but is not the thing that was asked for: sign-out should land on the page
   * you sign back in on. A document load rather than a router push, so nothing
   * from the ended session — cached surfaces, in-flight requests, overlay
   * state — survives it.
   */
  const handleSignOut = useCallback(async () => {
    await logout();
    window.location.assign("/login");
  }, [logout]);

  if (loading || !user) {
    return <ShellSignedOut loading={loading} />;
  }

  return (
    // The last resort. If the shell chrome itself throws there is nothing left
    // to preserve, so this boundary exists to render a message instead of a
    // white page — not to keep anything working. The boundary that actually
    // saves the session is the per-area one inside CipherShell.
    <ErrorBoundary label="The shell">
      <CipherShell user={user} onSignOut={handleSignOut}>
        <Routes>
          {/* The v1.1 entry point is the Brain — it is what replaces the generic
              /dashboard landing (§3.12). Nothing is unlinked to make that true;
              /dashboard stays exactly where it is. */}
          <Route index element={<Navigate to="brain" replace />} />

          {AREAS.map((area) => {
            const Area = AREA_COMPONENTS[area.id];
            // `<area>/*` rather than one route per tab: the tab is read from the
            // URL by the shell and handed to the surface through context, so an
            // area session adds a tab by adding one line to `areas.js` and one
            // entry to its own page map — never a route here, and never in App.js.
            return (
              <Route key={area.id} path={`${area.id}/*`} element={<Area />} />
            );
          })}

          {/* Anything else under /v2. Not a 404 page — that is the REDESIGN row
              in §3.11 and belongs to whoever builds the designed not-found and
              permission-denied states. Until then, the Brain. */}
          <Route path="*" element={<Navigate to="brain" replace />} />
        </Routes>
      </CipherShell>
    </ErrorBoundary>
  );
}
